import { observer } from "mobx-react-lite"
import AlertCircleOutline from "~icons/eva/alert-circle-outline"
import { CruiseSuggestionCard } from "../components/cards/CruiseSuggestionCard"
import styles from "../styles/cruiseList.module.scss"
import { DefaultSpinner } from "../components/loaders/DefaultSpinner"
import { useNavigate, useParams } from "react-router-dom"
import { getCruiseList } from "../services/firebase-functions/cruiseList"
import { useQuery } from "@tanstack/react-query"
import { TripSearchResult } from "../../../firebase/functions/src/libs/cruisen-contract/trips/trip-search.interface"
import { CruiseSearchResult, filterCruises, SelectedFilters } from "@cruisen/react-shared"
import { DownloadMobileAppBanner } from "../components/atoms/DownloadMobileAppBanner"
import { useMemo } from "react"
import { DownloadMobileAppModal } from "../components/modals/DownloadMobileAppModal"
import { ModalWrapper } from "../components/templates/ModalWrapper"
import { makeAutoObservable } from "mobx"
import { Helmet } from "react-helmet-async"

const transformCruiseList = (cruiseList?: TripSearchResult[]): CruiseSearchResult[] => {
  if (!cruiseList) {
    return []
  }

  return cruiseList.map((cruise) => ({
    id: cruise.id,
    docId: cruise.id,
    tripSearchId: cruise.tripSearchId,
    name: cruise.name,
    status: cruise.searchResultStatus,
    startDate: new Date(cruise.startDate),
    endDate: new Date(cruise.endDate),
    shipName: cruise.shipName,
    ports: cruise.ports,
    lineLogoImage: cruise.lineLogo,
    image: cruise.heroImage,
    price: cruise.lowestPrice,
    pricePP: cruise.lowestPricePP,
    cabinsScanStatus: cruise.cabinsScanStatus,
    shipLaunchDate: new Date(cruise.shipLaunchDate),
    lineId: cruise.lineId,
    lineName: cruise.lineName,
    likes: cruise.likes,
    tags: cruise.tags,
    cabinGradeTypes: cruise.cabinGradeTypes,
  }))
}

class CruiseListStore {
  isDownloadAppModalVisible: boolean

  constructor() {
    this.isDownloadAppModalVisible = false

    makeAutoObservable(this)
  }

  setIsDownloadAppModalVisible(isVisible: boolean) {
    this.isDownloadAppModalVisible = isVisible
  }
}

const cruiseListLength = 6

export const CruiseList = observer(function CruiseList() {
  const tripSearchId = "main"
  const navigate = useNavigate()
  const { tripId } = useParams()
  const store = useMemo(() => new CruiseListStore(), [])

  const { data: cruiseListData, isLoading } = useQuery({
    queryKey: ["cruiseList", tripId],
    queryFn: () => getCruiseList(tripId, tripSearchId),
  })

  const tripInviteLink = cruiseListData?.tripInviteLink
  const tripName = cruiseListData?.tripName ? `${cruiseListData.tripName} cruises` : "All cruises"
  const cruises = transformCruiseList(cruiseListData?.tripSearchResults)
  const filteredCruises = cruiseListData?.cruiseSearchPreferences
    ? filterCruises(cruises, cruiseListData?.cruiseSearchPreferences.filters as SelectedFilters)
    : cruises

  const cruisesAmount = filteredCruises.length
  const sortedCruises = filteredCruises.slice(0, cruiseListLength)

  return (
    <div className={styles.cruiseListPage} data-test-id="cruise-list-container">
      <Helmet>
        <title>{tripName}</title>
        <link rel="canonical" href={`${window.location.origin}/trips/${tripId}`} />
        <meta property="og:title" content={tripName} />
        <meta property="og:image" content={filteredCruises[0]?.image} />
        <meta property="og:url" content={`${window.location.origin}/trips/${tripId}`} />
        <meta property="og:site_name" content="Cruisen" />
        <meta name="og:type" content="website" />
        <meta name="twitter:title" content={tripName} />
        <meta name="twitter:image" content={filteredCruises[0]?.image} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <DownloadMobileAppBanner
        onInstall={() => {
          store.setIsDownloadAppModalVisible(true)
        }}
      />
      <div className={styles.cruiseListHeading}>
        {isLoading ? (
          <h1 className={styles.title}>Loading cruises...</h1>
        ) : (
          <div>
            <div className={styles.titleContainer}>
              <h1 className={styles.title}>All cruises</h1>
              <div className={styles.cruiseAmountContainer}>
                <p className={styles.cruiseAmount}>{cruisesAmount}</p>
              </div>
            </div>
            {cruiseListData?.cruiseSearchPreferences ? (
              <div className={styles.headingDescriptionContainer}>
                <p className={styles.headingDescription}>Pre-saved filtering</p>
                <AlertCircleOutline className={styles.headingDescriptionIcon} />
              </div>
            ) : null}
          </div>
        )}
      </div>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <DefaultSpinner />
        </div>
      ) : (
        <div className={styles.cruiseListWrapper}>
          {sortedCruises.map((cruise, index) => {
            return (
              <div className={styles.cruiseListCard} key={cruise.id}>
                <CruiseSuggestionCard
                  tripId={tripId}
                  userId={null}
                  onClick={() => {
                    navigate(`/trips/${tripId}/cruises/${cruise.id}`)
                  }}
                  cruise={cruise}
                  isSelected={false}
                  isSeperatePage={true}
                  testId={`cruise-${index + 1}`}
                  onLikeButtonClick={() => {
                    store.setIsDownloadAppModalVisible(true)
                  }}
                />
                {index === cruiseListLength - 1 && (
                  <div className={styles.moreCruises}>
                    <p className={styles.moreCruisesText}>
                      +{cruisesAmount - cruiseListLength} cruises available
                    </p>
                    <button
                      className={styles.moreCruisesButton}
                      onClick={() => {
                        store.setIsDownloadAppModalVisible(true)
                      }}
                    >
                      View all cruises
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      {store.isDownloadAppModalVisible && (
        <ModalWrapper>
          <DownloadMobileAppModal
            tripInviteLink={tripInviteLink}
            closeModal={() => store.setIsDownloadAppModalVisible(false)}
          />
        </ModalWrapper>
      )}
    </div>
  )
})
